import { sortLocalLiveStopsByEventOrder, sortLocalLiveUpdates } from "./localLive";
import { FALL_CRAWL_PASSPORT_KEY, isFallCrawlPassportIdentity } from "./fallCrawlPassport";

export const FALL_CRAWL_REVEAL_SESSION_PREFIX = "mfp:fall-crawl-public-reveal-preview";

export const FALL_CRAWL_REVEAL_DEFAULTS = Object.freeze({
  publicRevealMode: "teaser",
  stopsRevealMode: "hidden",
  featuredStopIds: [],
  mapRevealMode: "hidden",
  routeRevealMode: "hidden",
  routeFeaturedStopIds: [],
  backroadsBookRevealMode: "locked",
  passportCollectionEnabled: false,
  hostedMakersRevealMode: "hidden",
  featuredVendorIds: [],
  freshRevealKeys: [],
  lastRevealMode: "teaser",
  lastRevealSummary: "Public teaser mode is active.",
  lastRevealUpdatedAt: "",
});

export const FALL_CRAWL_REVEAL_PRESETS = Object.freeze({
  teaser: {
    publicRevealMode: "teaser",
    stopsRevealMode: "featured",
    mapRevealMode: "hidden",
    routeRevealMode: "hidden",
    backroadsBookRevealMode: "locked",
    passportCollectionEnabled: false,
    hostedMakersRevealMode: "hidden",
  },
  bookUnlock: {
    publicRevealMode: "book-unlock",
    stopsRevealMode: "featured",
    mapRevealMode: "hidden",
    routeRevealMode: "hidden",
    backroadsBookRevealMode: "unlocked-preview",
    passportCollectionEnabled: false,
    hostedMakersRevealMode: "hidden",
  },
  mapReveal: {
    publicRevealMode: "map-reveal",
    stopsRevealMode: "featured",
    mapRevealMode: "featured",
    routeRevealMode: "hidden",
    backroadsBookRevealMode: "unlocked-preview",
    passportCollectionEnabled: false,
    hostedMakersRevealMode: "featured",
  },
  routeReveal: {
    publicRevealMode: "route-reveal",
    stopsRevealMode: "all",
    mapRevealMode: "full",
    routeRevealMode: "full",
    backroadsBookRevealMode: "full",
    passportCollectionEnabled: false,
    hostedMakersRevealMode: "all",
  },
  eventDay: {
    publicRevealMode: "event-day",
    stopsRevealMode: "all",
    mapRevealMode: "full",
    routeRevealMode: "full",
    backroadsBookRevealMode: "full",
    passportCollectionEnabled: true,
    hostedMakersRevealMode: "all",
  },
});

const validModes = {
  publicRevealMode: new Set(["teaser", "book-unlock", "map-reveal", "route-reveal", "event-day"]),
  stopsRevealMode: new Set(["hidden", "featured", "all"]),
  mapRevealMode: new Set(["hidden", "featured", "full"]),
  routeRevealMode: new Set(["hidden", "partial", "full"]),
  backroadsBookRevealMode: new Set(["locked", "unlocked-preview", "full"]),
  hostedMakersRevealMode: new Set(["hidden", "featured", "all"]),
};

const buildRevealSummaryText = (reveal) => {
  const stops = reveal.stopsRevealMode === "all"
    ? "all confirmed stops"
    : reveal.stopsRevealMode === "featured"
      ? "featured stops"
      : "no individual stops";
  const map = reveal.mapRevealMode === "full"
    ? "full map"
    : reveal.mapRevealMode === "featured"
      ? "featured map"
      : "map hidden";
  const route = reveal.routeRevealMode === "full"
    ? "official route visible"
    : reveal.routeRevealMode === "partial"
      ? "partial route staged"
      : "route hidden";
  const book = reveal.backroadsBookRevealMode === "full"
    ? "Backroads Book fully revealed"
    : reveal.backroadsBookRevealMode === "unlocked-preview"
      ? "Backroads Book preview unlocked"
      : "Backroads Book locked";
  return `${stops}; ${map}; ${route}; ${book}; collection ${reveal.passportCollectionEnabled ? "enabled" : "off"}.`;
};

const asStringArray = (value) =>
  Array.isArray(value) ? [...new Set(value.map((item) => String(item || "").trim()).filter(Boolean))] : [];

const normalizeMode = (field, value) =>
  validModes[field]?.has(value) ? value : FALL_CRAWL_REVEAL_DEFAULTS[field];

export const getFallCrawlRevealSessionKey = (eventId = "") =>
  `${FALL_CRAWL_REVEAL_SESSION_PREFIX}:${eventId || "unknown-event"}`;

export const normalizeFallCrawlPublicReveal = (value = {}) => {
  const source = value && typeof value === "object" ? value : {};
  if (source.publicRevealMode && !validModes.publicRevealMode.has(source.publicRevealMode)) {
    return FALL_CRAWL_REVEAL_DEFAULTS;
  }
  const next = {
    ...FALL_CRAWL_REVEAL_DEFAULTS,
    publicRevealMode: normalizeMode("publicRevealMode", source.publicRevealMode),
    stopsRevealMode: normalizeMode("stopsRevealMode", source.stopsRevealMode),
    featuredStopIds: asStringArray(source.featuredStopIds),
    mapRevealMode: normalizeMode("mapRevealMode", source.mapRevealMode),
    routeRevealMode: normalizeMode("routeRevealMode", source.routeRevealMode),
    routeFeaturedStopIds: asStringArray(source.routeFeaturedStopIds),
    backroadsBookRevealMode: normalizeMode("backroadsBookRevealMode", source.backroadsBookRevealMode),
    passportCollectionEnabled: source.passportCollectionEnabled === true,
    hostedMakersRevealMode: normalizeMode("hostedMakersRevealMode", source.hostedMakersRevealMode),
    featuredVendorIds: asStringArray(source.featuredVendorIds),
    freshRevealKeys: asStringArray(source.freshRevealKeys),
    lastRevealMode: source.lastRevealMode || source.publicRevealMode || FALL_CRAWL_REVEAL_DEFAULTS.lastRevealMode,
    lastRevealSummary: source.lastRevealSummary || "",
    lastRevealUpdatedAt: source.lastRevealUpdatedAt || "",
  };
  return {
    ...next,
    lastRevealSummary: next.lastRevealSummary || buildRevealSummaryText(next),
  };
};

export const getFallCrawlPublicReveal = (event = {}, override = null) => {
  const sourceEvent = event || {};
  return normalizeFallCrawlPublicReveal(
    override ||
    sourceEvent.fallCrawlPublicReveal ||
    {
      publicRevealMode: sourceEvent.publicRevealMode,
      stopsRevealMode: sourceEvent.stopsRevealMode,
      featuredStopIds: sourceEvent.featuredStopIds,
      mapRevealMode: sourceEvent.mapRevealMode,
      routeRevealMode: sourceEvent.routeRevealMode,
      routeFeaturedStopIds: sourceEvent.routeFeaturedStopIds,
      backroadsBookRevealMode: sourceEvent.backroadsBookRevealMode,
      passportCollectionEnabled: sourceEvent.passportCollectionEnabled,
      hostedMakersRevealMode: sourceEvent.hostedMakersRevealMode,
      featuredVendorIds: sourceEvent.featuredVendorIds,
    }
  );
};

export const getFallCrawlRevealSummary = (config = {}) => {
  const reveal = normalizeFallCrawlPublicReveal(config);
  return buildRevealSummaryText(reveal);
};

export const applyFallCrawlRevealPreset = (current = {}, presetName = "teaser") => {
  const preset = FALL_CRAWL_REVEAL_PRESETS[presetName] || FALL_CRAWL_REVEAL_PRESETS.teaser;
  return normalizeFallCrawlPublicReveal({
    ...current,
    ...preset,
  });
};

const isEligiblePublicItem = (item = {}) =>
  item.publicEnabled === true && item.active === true;

const getStopsForMode = (orderedStops, reveal, mode) => {
  if (mode === "hidden") return [];
  if (mode === "all") return orderedStops;
  const featuredIds = new Set(reveal.featuredStopIds);
  return orderedStops.filter((stop) => featuredIds.has(stop.id));
};

const FALL_CRAWL_PARTIAL_ROUTE_SEGMENT_STOP_LIMIT = 8;

const getPartialRouteSegmentStops = (orderedStops, visibleMapStops) => {
  const visibleMapStopIds = new Set(visibleMapStops.map((stop) => stop.id));
  const segmentStops = [];
  let previousOrder = null;
  for (const stop of orderedStops) {
    if (!visibleMapStopIds.has(stop.id)) break;
    const currentOrder = Number(stop.order);
    if (
      segmentStops.length > 0 &&
      Number.isFinite(previousOrder) &&
      Number.isFinite(currentOrder) &&
      currentOrder !== previousOrder + 1
    ) {
      break;
    }
    segmentStops.push(stop);
    previousOrder = currentOrder;
    if (segmentStops.length >= FALL_CRAWL_PARTIAL_ROUTE_SEGMENT_STOP_LIMIT) break;
  }
  return segmentStops;
};

const filterVendors = (vendors, visibleStops, reveal) => {
  if (reveal.hostedMakersRevealMode === "hidden") return [];
  const visibleStopIds = new Set(visibleStops.map((stop) => stop.id));
  const eligibleVendors = vendors.filter((vendor) =>
    isEligiblePublicItem(vendor) && visibleStopIds.has(vendor.hostStopId)
  );
  if (reveal.hostedMakersRevealMode === "all") return eligibleVendors;
  const featuredVendorIds = new Set(reveal.featuredVendorIds);
  return eligibleVendors.filter((vendor) => featuredVendorIds.has(vendor.id));
};

export const buildFallCrawlPublicRevealView = ({
  event = {},
  stops = [],
  vendors = [],
  updates = [],
  override = null,
  adminFullPreview = false,
} = {}) => {
  const reveal = getFallCrawlPublicReveal(event, override);
  if (!isFallCrawlPassportIdentity(event || {})) {
    const orderedStops = sortLocalLiveStopsByEventOrder(stops);
    return {
      reveal: normalizeFallCrawlPublicReveal({
        ...reveal,
        stopsRevealMode: "all",
        mapRevealMode: "full",
        routeRevealMode: "full",
        hostedMakersRevealMode: "all",
        backroadsBookRevealMode: "full",
      }),
      stops: orderedStops,
      mapStops: orderedStops,
      routeStops: orderedStops,
      passportStops: orderedStops,
      vendors,
      updates: sortLocalLiveUpdates(updates),
      showMap: true,
      showRoute: true,
      showRouteNumbers: true,
      showRouteLanguage: true,
      showPartialRoutePreview: false,
      showPassport: false,
      passportRevealMode: "full",
      passportCollectionEnabled: false,
      passportMysteryMode: false,
      publicStatusLine: "",
    };
  }
  const orderedEligibleStops = sortLocalLiveStopsByEventOrder(
    adminFullPreview ? stops : stops.filter(isEligiblePublicItem)
  );

  if (adminFullPreview) {
    return {
      reveal,
      stops: orderedEligibleStops,
      mapStops: orderedEligibleStops,
      routeStops: orderedEligibleStops,
      passportStops: orderedEligibleStops,
      vendors,
      updates: sortLocalLiveUpdates(updates),
      showMap: true,
      showRoute: true,
      showRouteNumbers: true,
      showRouteLanguage: true,
      showPartialRoutePreview: false,
      showPassport: isFallCrawlPassportIdentity(event || {}),
      passportRevealMode: "full",
      passportCollectionEnabled: true,
      passportMysteryMode: false,
      publicStatusLine: "",
    };
  }

  const visibleStops = getStopsForMode(orderedEligibleStops, reveal, reveal.stopsRevealMode);
  const mapStops = reveal.mapRevealMode === "hidden"
    ? []
    : getStopsForMode(orderedEligibleStops, reveal, reveal.mapRevealMode === "full" ? "all" : "featured");
  const routeStops = reveal.routeRevealMode === "full"
    ? orderedEligibleStops
    : reveal.routeRevealMode === "partial"
      ? getPartialRouteSegmentStops(orderedEligibleStops, mapStops)
      : [];
  const showPartialRoutePreview = reveal.routeRevealMode === "partial" && reveal.mapRevealMode !== "hidden" && routeStops.length > 1;
  const passportFull = ["full", "event-day", "route-reveal"].includes(reveal.publicRevealMode) ||
    reveal.backroadsBookRevealMode === "full";
  const passportStops = passportFull
    ? orderedEligibleStops
    : reveal.backroadsBookRevealMode === "unlocked-preview"
      ? []
      : [];
  const showMap = reveal.mapRevealMode !== "hidden";

  return {
    reveal,
    stops: visibleStops,
    routeStops,
    mapStops,
    passportStops,
    vendors: filterVendors(vendors, visibleStops, reveal),
    updates: sortLocalLiveUpdates(updates.filter(isEligiblePublicItem)),
    showMap,
    showRoute: showPartialRoutePreview || (showMap && reveal.routeRevealMode === "full"),
    showRouteNumbers: reveal.routeRevealMode === "full",
    showRouteLanguage: reveal.routeRevealMode === "full",
    showPartialRoutePreview,
    showPassport: isFallCrawlPassportIdentity(event || {}) &&
      ["locked", "unlocked-preview", "full"].includes(reveal.backroadsBookRevealMode),
    passportRevealMode: reveal.backroadsBookRevealMode,
    passportCollectionEnabled: reveal.passportCollectionEnabled === true,
    passportMysteryMode: reveal.backroadsBookRevealMode === "unlocked-preview" && !passportFull,
    publicStatusLine: reveal.publicRevealMode === "teaser"
      ? "More Fall Crawl surprises are still on the way."
      : "New Fall Crawl details are being revealed as event day gets closer.",
  };
};

export const isFallCrawlRevealFresh = (timestamp, now = new Date(), hours = 48) => {
  if (!timestamp) return false;
  const date = new Date(timestamp);
  const current = now instanceof Date ? now : new Date(now);
  if (Number.isNaN(date.getTime()) || Number.isNaN(current.getTime())) return false;
  return current.getTime() - date.getTime() <= hours * 60 * 60 * 1000;
};

export const buildFallCrawlRevealEvent = (event = {}, revealOverride = null) => {
  const sourceEvent = event || {};
  return {
    ...sourceEvent,
    passportEventKey: sourceEvent.passportEventKey || FALL_CRAWL_PASSPORT_KEY,
    fallCrawlPublicReveal: getFallCrawlPublicReveal(sourceEvent, revealOverride),
  };
};
